import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ContractorPortalApi from "../../../../../infrastructure/api/contractorPortalApi";
import DefaultApiService from "../../../../../infrastructure/api/default";
import AcceptanceChecklist from "../components/AcceptanceChecklist";
import ContractorStatusBadge from "../components/ContractorStatusBadge";

const ContractorProjectAcceptanceTab = ({ executionId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await ContractorPortalApi.getAcceptance(executionId);
      if (cancelled) return;
      if (!res?.isSuccess) {
        setError(res?.message || "خطا در بارگذاری پذیرش.");
        setData(null);
      } else {
        setData(res.data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [executionId]);

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      const result = await DefaultApiService.uploadFile(
        files.map((file, index) => ({
          file,
          name: file.name || `acceptance-doc-${index + 1}`,
        })),
        "SLN"
      );
      if (result?.isSuccess === false) {
        toast.error(result?.message || "آپلود مدرک ناموفق بود.");
        return;
      }
      const rows = Array.isArray(result?.data) ? result.data : [];
      const paths = rows
        .map((r) => r.directory || r.Directory || r.name || r.Name)
        .filter(Boolean);
      setUploadedDocs((prev) => [...prev, ...paths]);
      toast.success("مدارک بارگذاری شد. تأیید نهایی فقط توسط سولارنت انجام می‌شود.");
    } catch (err) {
      toast.error(err?.message || "خطا در آپلود مدرک.");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const statusTitle = data?.finalAcceptance
    ? "پذیرش نهایی ثبت شده"
    : data?.latest
      ? data?.latest?.AcceptanceStatusTitle ||
        data?.latest?.acceptanceStatusTitle ||
        "در فرآیند پذیرش"
      : "هنوز درخواست پذیرش ثبت نشده";

  return (
    <Stack spacing={2} data-testid="contractor-acceptance-tab">
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              وضعیت پذیرش
            </Typography>
            <ContractorStatusBadge statusTitle={statusTitle} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={
                  data?.warrantyCompleted
                    ? "گارانتی تکمیل"
                    : "گارانتی ناقص"
                }
                color={data?.warrantyCompleted ? "success" : "default"}
                variant="outlined"
              />
              <Chip
                size="small"
                label={
                  data?.socialSecurityClearance
                    ? "مفاصا حساب OK"
                    : "مفاصا حساب ناقص"
                }
                color={data?.socialSecurityClearance ? "success" : "default"}
                variant="outlined"
              />
              <Chip
                size="small"
                label={data?.hasOpenDefects ? "ایراد باز دارد" : "بدون ایراد باز"}
                color={data?.hasOpenDefects ? "warning" : "success"}
                variant="outlined"
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <AcceptanceChecklist
        checklist={data?.checklist || []}
        missingRequirements={data?.missingRequirements || []}
      />

      <Card variant="outlined" data-testid="acceptance-feedback-card">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            بازخورد سولارنت
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.feedback || "بازخوردی ثبت نشده است."}
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined" data-testid="acceptance-upload-card">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            بارگذاری مدارک موردنیاز
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            پیمانکار نمی‌تواند پذیرش نهایی را تأیید کند. مدارک برای بررسی سولارنت
            بارگذاری می‌شوند.
          </Alert>
          <Button
            variant="outlined"
            component="label"
            data-testid="acceptance-upload-input"
          >
            انتخاب فایل
            <input
              hidden
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={(e) => handleUpload(e.target.files)}
            />
          </Button>
          {uploadedDocs.length ? (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {uploadedDocs.map((path) => (
                <Typography key={path} variant="caption" color="text.secondary">
                  {path}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ContractorProjectAcceptanceTab;
